import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';

const BASE_URL = 'https://www.3cat.cat';
const LANGS_ALL = ['ca', 'es', 'en', 'fr'];
const LANGS_ATENCIO = ['ca', 'es', 'en'];
const LANG_REGEX = /\/(ca|es|en|fr)\//;

const splitPath = (path) => {
    const [pathOnly, suffix = ''] = path.split(/(?=[?#])/);
    return { pathOnly, suffix };
};

const getCurrentLang = (path) => path.match(LANG_REGEX)?.[1] ?? 'ca';

const isMultiLangPath = (path) => {
    const { pathOnly } = splitPath(path);
    return /\/(atencio|tv3\/marato)(\/|$)/.test(pathOnly);
};

const buildPathWithLanguage = (path, targetLang) => {
    const { pathOnly, suffix } = splitPath(path);
    const hasTrailingSlash = pathOnly.endsWith('/');
    const parts = pathOnly.split('/').filter(Boolean);
    const langs = LANGS_ALL;

    // Detectar tipus de ruta
    const isAtencio = parts[0] === 'atencio';
    const isTv3Marato = parts[0] === 'tv3' && parts[1] === 'marato';

    if (!isAtencio && !isTv3Marato) return path;

    // Per a /atencio/ - l'idioma pot anar just després d''atencio' o després de la categoria
    // (p.ex. 'com-veure-i-escoltar'), segons la ruta concreta, per això cal localitzar-lo enlloc
    // d'assumir-ne una posició fixa.
    if (isAtencio) {
        const existingLangIndex = parts.findIndex((part) => langs.includes(part));
        if (existingLangIndex !== -1) {
            parts.splice(existingLangIndex, 1);
        }
        if (targetLang !== 'ca') {
            const anchorIndex = parts.lastIndexOf('com-veure-i-escoltar');
            const insertIndex = anchorIndex !== -1 ? anchorIndex + 1 : 1;
            parts.splice(insertIndex, 0, targetLang);
        }
    }

    // Per a /tv3/marato/ - l'idioma va al segment 2
    if (isTv3Marato) {
        if (parts.length >= 3 && langs.includes(parts[2])) {
            parts.splice(2, 1);
        }
        if (targetLang !== 'ca') {
            parts.splice(2, 0, targetLang);
        }
    }

    let newPath = `/${parts.join('/')}`;
    if (hasTrailingSlash && !newPath.endsWith('/')) newPath += '/';

    return `${newPath}${suffix}`;
};

const getAlternateUrl = (path, targetLang) => {
    if (isMultiLangPath(path)) {
        return `${BASE_URL}${buildPathWithLanguage(path, targetLang)}`;
    }

    return `${BASE_URL}${path.replace(LANG_REGEX, `/${targetLang}/`)}`;
};

const getLanguages = (path) => (isMultiLangPath(path) ? LANGS_ATENCIO : LANGS_ALL);

/**
 * Hook per actualitzar dinàmicament l'atribut lang de l'HTML i els tags alternate
 */
export const useLanguageUpdater = () => {
    const router = useRouter();

    useEffect(() => {
        const currentLang = getCurrentLang(router.asPath);

        if (typeof document !== 'undefined') {
            document.documentElement.lang = currentLang;
        }
    }, [router.asPath]);
};

/**
 * Component per generar els tags alternate dinàmicament
 */
export const AlternateTags = ({ hasMultiLanguage = false }) => {
    const { asPath } = useRouter();

    if (!hasMultiLanguage) return null;

    const currentLang = getCurrentLang(asPath);
    const languages = getLanguages(asPath);

    return (
        <Head key={asPath}>
            {languages
                .filter((lang) => lang !== currentLang)
                .map((lang) => (
                    <link key={`alternate-${lang}`} rel='alternate' hrefLang={lang} href={getAlternateUrl(asPath, lang)} />
                ))}
            <link key='alternate-x-default' rel='alternate' hrefLang='x-default' href={getAlternateUrl(asPath, 'ca')} />
        </Head>
    );
};
